import React, { useMemo } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import { renderObjective, renderStatus } from './helper';
import { ToggledText } from '../../screens/ViewCADD/components/input/comment';
import { Style } from './style';
import ActionsTable from './actionsTable';
import { ModalHeader } from 'datastake-daf/dist/components';

const layoutsWithNoRiskMb = ['LAYOUT_B'];

const getClassName = (layout) => {
  switch (layout) {
    case 'LAYOUT_B':
      return 'layout-b';
    default:
      return '';
  }
};

const ModalContent = ({
  rec,
  t = (s) => s,
  layout = 'DEFAULT',
  actions = {},
}) => {
  const riskConfig = useMemo(() => {
    if (typeof rec.score !== 'number') {
      return {
        className: layoutsWithNoRiskMb.includes(layout) ? `content risk` : 'content risk mb-0',
        label: '',
      };
    }
    const r = Math.floor(rec.score);
    let level = '';
    let label = '';

    switch (r) {
      case 0:
        level = 'very-low';
        label = t('Very Low');
        break;
      case 1:
        level = 'low';
        label = t('Low');
        break;
      case 2:
        level = 'medium';
        label = t('Medium');
        break;
      case 3:
        level = 'high';
        label = t('High');
        break;
      case 4:
        level = 'very-high';
        label = t('Very High');
        break;
      default:
        level = 'very-high';
        label = t('Very High');
    }

    return {
      className: layoutsWithNoRiskMb.includes(layout) ? `content risk ${level}` : `content mb-0 risk ${level}`,
      label,
    };
  }, [rec.score, layout]);

  const renderedRisk = () => {
    switch (layout) {
      case 'LAYOUT_B':
        return (
          <div className="section">
            <h2>{t('Risk')}</h2>
            {typeof rec.score === 'number' && (
              <div>
                <p className="title">{t('Risk')}</p>
                <p className={riskConfig.className}>
                  {`${t(riskConfig.label)}: ${rec.score}`}
                </p>
              </div>
            )}
            <div>
              <p className="title">{t('Risk Category')}</p>
              <p className="content">{rec.riskCategory}</p>
            </div>
            <div>
              <p className="title">{t('Dashboard::Risk Type')}</p>
              <p className="content">{rec.riskType}</p>
            </div>
          </div>
        );
      default:
        return (
          <div className="section">
            <h2>{t('Risk')}</h2>
            <div>
              <p className="title">{t('Dashboard::Risk Type')}</p>
              <p className="content">{rec.riskType}</p>
            </div>
            <div>
              <p className="title">{t('Risk Category')}</p>
              <p className="content">{rec.riskCategory}</p>
            </div>
            {typeof rec.score === 'number' && (
              <div>
                <p className="title">{t('Risk')}</p>
                <p className={riskConfig.className}>
                  {`${t(riskConfig.label)}: ${rec.score}`}
                </p>
              </div>
            )}
          </div>
        );
    }

  };

  const renderedDate = () => {
    switch (layout) {
      case 'LAYOUT_B':
        return null;
      default:
        return (
          <div>
            <h2>{t('Proposed Date')}</h2>
            <div className="date mb-0">
              <CalendarOutlined style={{ fontSize: '14px' }} />
              <span>{moment(rec.date).format("D MMM YYYY")}</span>
            </div>
          </div>
        );
    }
  };

  const renderedImplementer = () => {
    switch (layout) {
      case 'LAYOUT_B':
        return (
          <div className="designated-implementer">
            <h2>{t('Designated Implementer')}</h2>
            <div className="info">
              <div className="item">
                <span>{t('Country')}</span>
                <span className="value">{rec.stakeholder.country || '-'}</span>
              </div>
              <div className="item">
                <span>{t('Stakeholder Category')}</span>
                <span className="value">{rec.stakeholder.category || '-'}</span>
              </div>
              <div className="item">
                <span>{t('Stakeholder Sub-category')}</span>
                <span className="value">{rec.stakeholder.subCategory || '-'}</span>
              </div>
              <div className="item">
                <span>{t('Name')}</span>
                <span className="value">{rec.stakeholder.name || '-'}</span>
              </div>
              <div className="item">
                <span>{t('ID')}</span>
                <span className="value">{rec.stakeholder.id || '-'}</span>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="designated-implementer">
            <h2>{t('Designated Implementer')}</h2>
            <div className="info">
              <div className="item">
                <span>{t('Name')}</span>
                <span className="value">{rec.stakeholder.name}</span>
              </div>
              <div className="item">
                <span>{t('Stakeholder Category')}</span>
                <span className="value">{rec.stakeholder.category}</span>
              </div>
              <div className="item">
                <span>{t('Stakeholder Sub-category')}</span>
                <span className="value">{rec.stakeholder.subCategory}</span>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderedAssociatedActions = () => {
    switch (layout) {
      case 'LAYOUT_B':
        return (
          <div className="associated-actions">
            <h2>{t('Associated Actions')}</h2>
            <ActionsTable
              actions={(rec.events || []).filter((r) => typeof r !== 'string')}
              t={t}
            />
          </div>
        );
      default:
        return null;
    }
  }

  return rec ? (
    <Style className={getClassName(layout)}>
      <div className="data">
        <div className="section">
          <h2>{t('Scope')}</h2>
          {rec.typeOfMeasure && (
            <div>
              <p className="title">{t('Type of Measure')}</p>
              <p className="content">{rec.typeOfMeasure}</p>
            </div>
          )}
          {rec.objective && (
            <div>
              <p className="title">{t('Objective')}</p>
              <div className="content">
                {typeof actions.renderObjective === 'function' ?
                  actions.renderObjective({ t, label: rec.objective })
                  : renderObjective(t, rec.objective)}
              </div>
            </div>
          )}
          {(rec.status || rec.completionStatus) && (
            <div>
              <p className="title">{t('Status')}</p>
              <div className="content mb-0">
                {typeof actions.renderStatus === 'function' ?
                  actions.renderStatus({ t, status: rec.completionStatus || rec.status })
                  : renderStatus(t, rec.completionStatus || rec.status, true)}
              </div>
            </div>
          )}
        </div>
        <div className="section">
          <h2>{t('Location')}</h2>
          {rec.locationType && (
            <div>
              <p className="title">{t('Location Type')}</p>
              <p className="content">{rec.locationType}</p>
            </div>
          )}
          <div>
            <p className="title">{t('Dashboard::Location Name')}</p>
            <p className="content mb-0">{rec.area}</p>
          </div>
        </div>
        {renderedRisk()}
      </div>
      {rec.date && renderedDate()}
      {rec.details && (
        <div className="details">
          <h2>{t('Details')}</h2>
          <ToggledText
            t={t}
            text={(rec.details || '').length ? rec.details : t('Unavailable')}
            viewClassName="details-view"
          />
        </div>
      )}
      {rec.stakeholder && renderedImplementer()}
      {renderedAssociatedActions()}
    </Style>
  ) : null;
};

export const MeasureModalHeader = ({
  rec,
  t = (s) => s,
  informationSource = t('Me'),
  layout = 'DEFAULT',
}) => {
  const headerConfig = () => {
    switch (layout) {
      case 'LAYOUT_B':
        return {
          title: `${t('By')} ${informationSource}`,
          tags: rec.updatedAt ?  [{ label: moment(rec.updatedAt).format("D MMM YYYY") }] : [],
        }
      default:
        return {
          title: informationSource,
          tags: rec.updatedAt ?  [{ label: moment(rec.updatedAt).format("D MMM YYYY") }] : [],
        }
    }
  };

  return <ModalHeader {...headerConfig()} />;
};

export default ModalContent;
